import React from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { hideCommentsSidebar } from '../../../store/CommentsSlice';
import CommentsSidebar from '../../Comments/components/CommentsSidebar';
import CommentBox from '../../Comments/components/CommentBox';
import useCommentActions from '../../Comments/hooks/useCommentActions';
import { useUserContext } from '../../../context/user/UserContext';
import useClipComments from '../../Comments/hooks/useClipComments';
import { usePromptsContext } from '../../../context/prompts/PromptsContext';
import { findTeamMember } from '../../Team/teamUtils';

interface StudioClipCommentsProps {
  projectId: string;
}

/**
 * Clip comments sidebar wrapper
 * Handles data fetching
 */
const StudioClipComments = ({ projectId }: StudioClipCommentsProps) => {
  const commentsState = useAppSelector(appState => appState.comments);
  const { comments, loading, error, refetch } = useClipComments({
    clipId: commentsState.clipId,
  });
  const [errors, setErrors] = React.useState(
    comments.reduce<Record<string, string | undefined>>((acc, current) => {
      acc[current.id] = '';
      return acc;
    }, {})
  );

  const dispatch = useAppDispatch();
  const { data: userData } = useUserContext();
  const [, promptsDispatch] = usePromptsContext();

  const handleRefresh = () => {
    refetch();
  };
  const handleClose = () => {
    dispatch(hideCommentsSidebar());
  };
  const { onSave, onViewed, onCancel, onDelete, onMarkAllAsViewed, onSend } =
    useCommentActions({
      clipId: commentsState.clipId,
      projectId,
      setErrors,
    });

  const handleDelete = (id: string) => {
    promptsDispatch({
      type: 'OpenDeleteClipCommentConfirmationPrompt',
      payload: {
        onConfirm: () => onDelete(id),
      },
    });
  };

  const commentItems = React.useMemo(() => {
    const items = comments
      .map((comment, index) => ({
        ...comment,
        // Viewer user id
        userId: userData?.user.id || '',
        authorUserId: comment.userId,
        index,
        text: comment.content,
        deletedAt: comment.deletedAt || undefined,
        // who created this comment , not necessarily the viewer but it could be
        commentOwner: findTeamMember(userData?.user.team, comment.userId),
        viewed: comment.selfViewed,
      }))
      .sort((a, b) => Number(a.createdAt) - Number(b.createdAt));

    return items;
  }, [comments, userData?.user.id, userData?.user.team?.members]);

  return (
    <CommentsSidebar
      clipId={commentsState.clipId}
      clipName={commentsState.clipName}
      comments={commentItems}
      loading={loading}
      error={error}
      renderComment={({ item }) => (
        <CommentBox
          key={item.id}
          {...item}
          error={errors[item.id]}
          onSave={onSave}
          onDelete={handleDelete}
          onCancel={onCancel}
          onViewed={onViewed}
        />
      )}
      onMarkAllAsViewed={onMarkAllAsViewed}
      onSend={onSend}
      onClose={handleClose}
      onRefresh={handleRefresh}
    />
  );
};

export default StudioClipComments;
